import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, Phone } from "lucide-react";

const STEPS = [
  {
    num: "01", title: "Apply",
    text: "Submit a short application telling us what you're after — species, destination, timing, and budget. Takes two minutes. No commitment.",
    detail: "We read every application personally. No bots. No autoresponders.",
  },
  {
    num: "02", title: "Talk to Your Advisor",
    text: "Within 24 hours, one of our experienced advisors calls you directly. This isn't a sales pitch — it's a real conversation about your goals, experience, and what matters to you on this hunt.",
    detail: "Most calls run 20-30 minutes. Come with questions.",
  },
  {
    num: "03", title: "Get Matched",
    text: "Based on your conversation, your advisor recommends specific hunts, outfitters, and timing. You'll get honest assessments — what's great, what's not worth it, and why.",
    detail: "Every recommendation comes from direct experience, not a brochure.",
  },
  {
    num: "04", title: "Book & Deposit",
    text: "Once you've decided, a deposit locks your dates. We handle outfitter agreements, payment schedules, and cancellation terms so everything is clear upfront.",
    detail: "No hidden fees. No surprise add-ons.",
  },
  {
    num: "05", title: "Pre-Trip Prep",
    text: "Flights, licensing, permits, gear lists, physical preparation, travel insurance, outfitter communication — we handle it all. You get a complete pre-trip packet.",
    detail: "You'll know exactly what to expect before you leave home.",
  },
  {
    num: "06", title: "The Hunt",
    text: "You're in the field. We're standing by. If something comes up — weather, logistics, changes — we're reachable and ready to help.",
    detail: "Your focus should be on the hunt, not the details.",
  },
  {
    num: "07", title: "Post-Hunt",
    text: "Debrief with your advisor. Trophy logistics and taxidermy coordination if needed. Honest assessment of the outfitter and the experience. And when you're ready — start planning the next one.",
    detail: "Most of our clients come back. That's the whole point.",
  },
];

const FAQS = [
  { q: "How much does it cost to use Dispatch Hunts?", a: "Our advisory service is free to clients. We earn a commission from the outfitter. Your price is the same whether you book direct or through us — but you get our team's expertise, vetting, and full logistics support at no extra cost." },
  { q: "Can you help with hunts you don't list?", a: "Yes. If you have a specific hunt in mind, we can vet the outfitter, review the operation, and advise on whether it's worth your money. We're not limited to our listed expeditions." },
  { q: "How far in advance should I book?", a: "For premium hunts (Alaska brown bear, Central Asia, Africa), 6-12 months is standard. Some high-demand hunts book 12-18 months out. The sooner you start the conversation, the better your options." },
  { q: "What if I need to cancel?", a: "Cancellation terms vary by outfitter and are spelled out before you book. We help you understand the terms upfront so there are no surprises. Many hunts can be rescheduled with advance notice." },
  { q: "Do you handle international travel and licensing?", a: "Yes. Flights, CITES permits, import/export paperwork, firearms transport, travel insurance, visas — we handle or coordinate all of it." },
];

export default function Process() {
  return (
    <>
      <Helmet>
        <title>The Process | Dispatch Hunts</title>
        <meta name="description" content="Seven steps from consultation to campfire. How Dispatch Hunts handles every detail of your expedition." />
      </Helmet>

      {/* Hero */}
      <section className="section-dark grain pt-28 pb-16" data-testid="process-hero">
        <div className="container-dh text-center max-w-3xl mx-auto">
          <p className="text-label text-brass mb-4">How It Works</p>
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-bone mb-4" data-testid="process-headline">
            Your hunt, handled start to finish.
          </h1>
          <p className="font-body text-bone/50 max-w-xl mx-auto">
            Seven steps. One point of contact. Every detail covered so you can focus on the moment.
          </p>
        </div>
      </section>

      {/* Steps */}
      <section className="section-light py-20 lg:py-28" data-testid="process-steps">
        <div className="container-dh max-w-3xl">
          {STEPS.map((step, i) => (
            <div key={step.num} className={`flex gap-6 sm:gap-10 ${i < STEPS.length - 1 ? "pb-14 mb-14 border-b border-carbon/10" : ""}`} data-testid={`process-step-${step.num}`}>
              <div className="flex-shrink-0">
                <span className="font-mono text-4xl sm:text-5xl font-bold text-brass/20">{step.num}</span>
              </div>
              <div>
                <h3 className="font-display text-2xl font-bold text-carbon mb-3">{step.title}</h3>
                <p className="font-body text-slate leading-relaxed mb-3">{step.text}</p>
                <p className="font-body text-sm text-smoke italic">{step.detail}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="section-chalk py-20 lg:py-28" data-testid="process-faq">
        <div className="container-dh max-w-3xl">
          <p className="text-label text-brass mb-3">Common Questions</p>
          <h2 className="font-display text-3xl font-bold text-carbon mb-12">Straight answers.</h2>
          <div className="space-y-8">
            {FAQS.map((f) => (
              <div key={f.q} className="pb-8 border-b border-carbon/10">
                <h4 className="font-display text-lg font-bold text-carbon mb-3">{f.q}</h4>
                <p className="font-body text-slate leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-dark grain py-20" data-testid="process-cta">
        <div className="container-dh text-center max-w-2xl mx-auto">
          <h2 className="font-display text-3xl font-bold text-bone mb-4">Step one starts here.</h2>
          <p className="font-body text-bone/50 mb-8">Apply for a consultation. We'll call you within 24 hours.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/apply" className="btn-brass" data-testid="process-apply-btn">Apply for a Consultation <ArrowRight className="w-4 h-4" /></Link>
            <a href="tel:4065805366" className="btn-outline"><Phone className="w-4 h-4" /> 406-580-5366</a>
          </div>
        </div>
      </section>
    </>
  );
}
